// InputManager - per-player keyboard or mouse input handling

import Phaser from 'phaser';
import type { InputBinding } from '@/types/multiplayer';

type InputDownCallback = (time: number) => void;
type InputUpCallback = (time: number, duration: number) => void;

export class InputManager {
  private scene: Phaser.Scene;
  private enabled: boolean = false;
  private isPressed: boolean = false;
  private pressStartTime: number = 0;

  private inputKey: Phaser.Input.Keyboard.Key | null = null;
  private onDownCallbacks: InputDownCallback[] = [];
  private onUpCallbacks: InputUpCallback[] = [];
  private binding: InputBinding;

  private pointerDownHandler?: (pointer: Phaser.Input.Pointer) => void;
  private pointerUpHandler?: (pointer: Phaser.Input.Pointer) => void;

  constructor(scene: Phaser.Scene, binding: InputBinding) {
    this.scene = scene;
    this.binding = binding;
    this.setupInput();
  }

  private setupInput(): void {
    if (this.binding.type === 'keyboard') {
      this.setupKeyboard(this.binding.key);
    } else {
      this.setupMouse(this.binding.mouseButton);
    }
  }

  private setupKeyboard(key: string): void {
    const normalizedKey = key.toUpperCase();
    const keyCode = Phaser.Input.Keyboard.KeyCodes[
      normalizedKey as keyof typeof Phaser.Input.Keyboard.KeyCodes
    ] || Phaser.Input.Keyboard.KeyCodes.SPACE;

    this.inputKey = this.scene.input.keyboard?.addKey(keyCode) || null;

    if (this.inputKey) {
      this.inputKey.on('down', () => this.handleDown());
      this.inputKey.on('up', () => this.handleUp());
    }
  }

  private setupMouse(button: number): void {
    this.pointerDownHandler = (pointer: Phaser.Input.Pointer) => {
      if (pointer.button !== button) return;
      this.handleDown();
    };

    this.pointerUpHandler = (pointer: Phaser.Input.Pointer) => {
      if (pointer.button !== button) return;
      this.handleUp();
    };

    this.scene.input.on('pointerdown', this.pointerDownHandler);
    this.scene.input.on('pointerup', this.pointerUpHandler);
  }

  private handleDown(): void {
    if (!this.enabled || this.isPressed) return;

    this.isPressed = true;
    this.pressStartTime = this.scene.time.now;

    for (const callback of this.onDownCallbacks) {
      callback(this.pressStartTime);
    }
  }

  private handleUp(): void {
    if (!this.enabled || !this.isPressed) return;

    const time = this.scene.time.now;
    const duration = time - this.pressStartTime;

    this.isPressed = false;

    for (const callback of this.onUpCallbacks) {
      callback(time, duration);
    }
  }

  onInputDown(callback: InputDownCallback): void {
    this.onDownCallbacks.push(callback);
  }

  onInputUp(callback: InputUpCallback): void {
    this.onUpCallbacks.push(callback);
  }

  enable(): void {
    this.enabled = true;
  }

  disable(): void {
    this.enabled = false;
    this.isPressed = false;
  }

  cancelPress(): void {
    this.isPressed = false;
    this.pressStartTime = 0;
  }

  destroy(): void {
    this.disable();
    this.onDownCallbacks = [];
    this.onUpCallbacks = [];

    if (this.inputKey) {
      this.inputKey.removeAllListeners();
    }

    if (this.pointerDownHandler) {
      this.scene.input.off('pointerdown', this.pointerDownHandler);
    }
    if (this.pointerUpHandler) {
      this.scene.input.off('pointerup', this.pointerUpHandler);
    }
  }
}
